// @ts-check
/* Pure helpers — no shared state dependencies. */

/**
 * Haptic feedback (V4). Trygg på alle plattformer — null-op når ikke støttet.
 * @param {'light'|'medium'|'heavy'|'success'|'warning'|'error'|'select'} kind
 */
export function haptic(kind='light'){
  try {
    if (!('vibrate' in navigator)) return;
    const map = {
      light:8, medium:14, heavy:24, select:6,
      success:[8,40,12], warning:[12,40,12], error:[20,60,20,60,20],
    };
    navigator.vibrate(map[kind] ?? 8);
  } catch {}
}

/**
 * Wrap a DOM-mutating callback i View Transitions API hvis støttet.
 * Faller tilbake til vanlig kall ellers. Brukes for fane-bytte.
 */
export function withTransition(fn){
  if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const t = document.startViewTransition(fn);
    // Rask fanebytting avbryter forrige transition → promisene avvises med
    // «Transition was skipped». Uhåndtert havnet det i feilloggen og utløste
    // falsk «Noe gikk galt»-toast. Avbrutt transition er normalt — svelg det.
    t.finished?.catch(()=>{});
    t.ready?.catch(()=>{});
    t.updateCallbackDone?.catch(()=>{});
    return t;
  }
  fn();
  return null;
}

/**
 * fetch med hard timeout via AbortController. Et vanlig fetch() avviser bare
 * ved nettverks*feil*, ikke når socket-en henger (captive portals på hotell/
 * cruise/Alaska) — da blir promiset hengende for alltid og UI-en viser «laster …»
 * i det uendelige. Med timeout abortes kallet, fetch avviser, og kallernes
 * eksisterende catch-grener får vist sin graceful fallback.
 * @param {string} url
 * @param {RequestInit & {timeoutMs?:number}} [opts]
 */
export function fetchWithTimeout(url, opts = {}) {
  const { timeoutMs = 8000, ...rest } = opts;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  return fetch(url, { ...rest, signal: ctrl.signal }).finally(() => clearTimeout(t));
}

export function esc(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Slipper bare gjennom http(s)-URLer — alt annet (javascript:, data:, eller
 * et innslag som «x" onerror=...») blir til tom streng. Brukes på URLer som
 * stammer fra databasen, der anon-nøkkelen tillater at hvem som helst skriver.
 * @param {string} u
 * @returns {string}
 */
export function safeUrl(u) {
  const s = String(u || "").trim();
  return /^https?:\/\//i.test(s) ? s : "";
}

export function irow(l, v, ph) {
  return `<div class="irow"><span class="irow-l">${esc(l)}</span><span class="irow-v${ph ? " phone" : ""}">${esc(v)}</span></div>`;
}

export function wbox(cls, html) {
  return `<div class="wbox ${cls}">${html}</div>`;
}

// Great-circle distance in km (Haversine).
export function distKm(a, b) {
  const R = 6371, tr = x => x * Math.PI / 180;
  const dLa = tr(b.lat - a.lat), dLn = tr(b.lng - a.lng);
  const la1 = tr(a.lat), la2 = tr(b.lat);
  const h = Math.sin(dLa / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLn / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Detect once: does the canvas actually emit WebP, or silently fall back to PNG?
let _webpOk = null;
function supportsWebp() {
  if (_webpOk !== null) return _webpOk;
  try {
    const c = document.createElement("canvas");
    c.width = c.height = 1;
    _webpOk = c.toDataURL("image/webp").startsWith("data:image/webp");
  } catch { _webpOk = false; }
  return _webpOk;
}

// data:-URL → Blob uten fetch(). CSP-en vår har connect-src UTEN data:,
// så `fetch(dataUrl)` kaster «Failed to fetch» — dekod base64 direkte i stedet.
export function dataUrlToBlob(dataUrl) {
  const [head, b64] = dataUrl.split(",");
  const mime = (/data:([^;]+)/.exec(head) || [])[1] || "application/octet-stream";
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// Client-side image compression via canvas. Returns { dataUrl, mime, ext }.
// Prefers WebP (~25-35% smaller than JPEG at same quality) with JPEG fallback.
export function compressImage(file, maxW, quality) {
  return new Promise(res => {
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const useWebp = supportsWebp();
        const mime = useWebp ? "image/webp" : "image/jpeg";
        const ext = useWebp ? "webp" : "jpg";
        res({ dataUrl: canvas.toDataURL(mime, quality), mime, ext });
      };
      img.src = /** @type {string} */ (ev.target.result);
    };
    reader.readAsDataURL(file);
  });
}
